module.exports = async function initApplicationArtisanReportClient(context) {
  if (typeof document === "undefined") {
    return;
  }
  var currentDocument = document;
  var currentWindow = typeof window === "undefined" ? {} : window;
  var currentRoot = currentDocument.getElementById("application-artisan-report-root");
  if (!(currentRoot)) {
    return;
  }

  var dashboardApi = (context || {}).dashboardApi || currentWindow.DashboardAPI || ((((currentWindow.UATools || {}).dashboard || {}).api) || null);
  var pageNamespace = (currentRoot.getAttribute("data-page-namespace") || "platform.application.artisan.report").toString();
  var apiUrl = (currentRoot.getAttribute("data-api-url") || "").toString().trim();
  var status = currentDocument.getElementById("application-artisan-report-status");
  var latestGeneratedOn = (currentRoot.getAttribute("data-generated-on") || "").toString();

  function getRoot() {
    return currentDocument.getElementById("application-artisan-report-root") || currentRoot;
  }

  function getGenerateButtons() {
    return Array.from(currentDocument.querySelectorAll(
      "#application-artisan-report-generate,button[data-dashboard-action='generate-report']"
    ));
  }

  function setGeneratingState(isGenerating) {
    var root = getRoot();
    if (!(root)) {
      return;
    }

    root.setAttribute("data-generating", isGenerating ? "true" : "false");
  }

  function createDefaultGenerateButtonContent() {
    var icon = currentDocument.createElement("i");
    icon.className = "bi bi-arrow-repeat me-2";
    icon.setAttribute("aria-hidden", "true");

    return [icon, currentDocument.createTextNode("Generate Report")];
  }

  function captureGenerateButtonContent(button) {
    if (!button || button.__artisanOriginalNodes instanceof Array) {
      return;
    }

    var childNodes = Array.from(button.childNodes || []);
    button.__artisanOriginalNodes = childNodes.length ? childNodes.map(function(node) {
      return node.cloneNode(true);
    }) : createDefaultGenerateButtonContent();
  }

  function replaceButtonChildren(button, nodes) {
    if (!button) {
      return;
    }

    button.replaceChildren.apply(button, (nodes || []).map(function(node) {
      return node.cloneNode(true);
    }));
  }

  function setGenerateButtonsState(isGenerating) {
    var buttons = getGenerateButtons();

    for (var index = 0; index < buttons.length; index += 1) {
      var button = buttons[index];
      captureGenerateButtonContent(button);

      button.disabled = isGenerating === true;
      button.setAttribute("aria-busy", isGenerating ? "true" : "false");

      if (isGenerating) {
        var busyIcon = currentDocument.createElement("i");
        busyIcon.className = "bi bi-arrow-repeat me-2";
        busyIcon.setAttribute("aria-hidden", "true");
        replaceButtonChildren(button, [
          busyIcon,
          currentDocument.createTextNode("Generating")
        ]);
      } else {
        replaceButtonChildren(button, button.__artisanOriginalNodes || createDefaultGenerateButtonContent());
      }
    }
  }

  function clearBusyState(message) {
    setGeneratingState(false);
    setGenerateButtonsState(false);

    if (status) {
      status.setAttribute("data-busy", "false");
      status.replaceChildren();

      if (message) {
        status.appendChild(currentDocument.createTextNode(message));
      }
    }
  }

  function showPlatformLoading() {
    try {
      if (currentWindow.UATools && currentWindow.UATools.loading) {
        currentWindow.UATools.loading.manual();
        currentWindow.UATools.loading.show();
      }
    } catch (error) {
      console.warn("Application Artisan Report loading show failed.", error);
    }
  }

  function hidePlatformLoading() {
    try {
      if (currentWindow.UATools && currentWindow.UATools.loading) {
        currentWindow.UATools.loading.hide();
      }
    } catch (error) {
      console.warn("Application Artisan Report loading hide failed.", error);
    }
  }

  function setStatus(message, busy) {
    if (!(status)) {
      return;
    }

    status.replaceChildren();

    if (busy) {
      status.appendChild(Object.assign(currentDocument.createElement("span"), {
        className: "spinner-border spinner-border-sm me-2",
        role: "status"
      }));
    }

    status.appendChild(currentDocument.createTextNode(message));
    status.setAttribute("data-busy", busy ? "true" : "false");
    setGeneratingState(busy);
    setGenerateButtonsState(busy);
  }

  async function pause(ms) {
    await new Promise(function(resolve) {
      currentWindow.setTimeout(resolve, ms);
    });
  }

  async function runDashboardAction(actionName, data) {
    return await dashboardApi.action(pageNamespace, "action", {
      action: actionName,
      data: data || {}
    }, apiUrl || null);
  }

  async function replaceReportView(content) {
    if (!(content) || !(currentWindow.UATools) || typeof currentWindow.UATools.render !== "function") {
      return false;
    }

    showPlatformLoading();

    try {
      var rendered = await currentWindow.UATools.render(content);
      var nextRoot = null;

      if (typeof rendered === "string") {
        var holder = currentDocument.createElement("div");
        holder.innerHTML = rendered.trim();
        nextRoot = holder.firstElementChild;
      } else if (rendered && rendered.nodeType) {
        nextRoot = rendered;
      }

      var root = getRoot();
      if (!(root) || !(root.parentNode) || !(nextRoot)) {
        return false;
      }

      if (!(nextRoot.getAttribute("data-api-url")) && apiUrl) {
        nextRoot.setAttribute("data-api-url", apiUrl);
      }

      root.parentNode.replaceChild(nextRoot, root);
      currentRoot = nextRoot;
      status = currentDocument.getElementById("application-artisan-report-status");
      latestGeneratedOn = (nextRoot.getAttribute("data-generated-on") || latestGeneratedOn || "").toString();

      if (typeof currentWindow.UATools.after === "function") {
        currentWindow.UATools.after();
      }

      await module.exports(context);
      clearBusyState("Latest report loaded.");
      return true;
    } finally {
      hidePlatformLoading();
    }
  }

  function getIntersectionObserver() {
    if (typeof currentWindow.IntersectionObserver === "function") {
      return currentWindow.IntersectionObserver;
    }

    if (typeof IntersectionObserver === "function") {
      return IntersectionObserver;
    }

    return null;
  }

  function animateNumbers() {
    var root = getRoot();
    if (!(root)) {
      return;
    }

    var numberNodes = root.querySelectorAll("[data-artisan-number]");
    if (!(numberNodes.length)) {
      return;
    }

    function animateNode(node) {
      if (!node || node.getAttribute("data-artisan-animated") === "true") {
        return;
      }

      node.setAttribute("data-artisan-animated", "true");

      var finalValue = Number(node.getAttribute("data-artisan-number") || 0);
      var duration = node.closest(".app-artisan-metric-card-featured") ? 4200 : 3200;
      var startedAt = 0;
      node.textContent = "0";

      function tick(now) {
        if (!(startedAt)) {
          startedAt = now;
        }

        var progress = Math.min(1, (now - startedAt) / duration);
        var eased = 1 - Math.pow(1 - progress, 3);
        node.textContent = Math.round(finalValue * eased).toLocaleString("en-US");

        if (progress < 1) {
          currentWindow.requestAnimationFrame(tick);
        }
      }

      currentWindow.requestAnimationFrame(tick);
    }

    function isVisibleInViewport(node) {
      var rect = node.getBoundingClientRect();
      var viewportHeight = currentWindow.innerHeight || ((currentDocument.documentElement || {}).clientHeight) || 0;
      return rect.top < (viewportHeight * 0.92) && rect.bottom > 0;
    }

    var IntersectionObserverConstructor = getIntersectionObserver();
    var observer = IntersectionObserverConstructor ? new IntersectionObserverConstructor(function(entries) {
      for (var index = 0; index < entries.length; index += 1) {
        var entry = entries[index];
        if (!(entry.isIntersecting)) {
          continue;
        }

        animateNode(entry.target);
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.1
    }) : null;

    numberNodes.forEach(function(node) {
      node.removeAttribute("data-artisan-animated");

      if (isVisibleInViewport(node) || !(observer)) {
        animateNode(node);
        return;
      }

      observer.observe(node);
    });
  }

  function animatePopItems() {
    var root = getRoot();
    var nodes = root.querySelectorAll("[data-artisan-pop]");
    if (!(nodes.length)) {
      return;
    }

    var IntersectionObserverConstructor = getIntersectionObserver();
    if (!(IntersectionObserverConstructor)) {
      nodes.forEach(function(node) {
        if (node && node.classList && typeof node.classList.add === "function") {
          node.classList.add("is-visible");
        }
      });
      return;
    }

    var observer = new IntersectionObserverConstructor(function(entries) {
      for (var index = 0; index < entries.length; index += 1) {
        if (entries[index].isIntersecting) {
          entries[index].target.classList.add("is-visible");
        } else {
          entries[index].target.classList.remove("is-visible");
        }
      }
    }, {
      threshold: 0.18
    });

    nodes.forEach(function(node) {
      observer.observe(node);
    });
  }

  async function pollForFreshReport(previousGeneratedOn) {
    if (!(dashboardApi)) {
      return;
    }

    var attempts = 0;
    var maxAttempts = 40;
    var pollFailures = 0;

    while (attempts < maxAttempts) {
      attempts += 1;

      try {
        var response = await runDashboardAction("read-latest", {});
        var data = (response || {}).data || {};
        var generatedOn = (data.generatedOn || "").toString();

        if (generatedOn && generatedOn !== previousGeneratedOn) {
          setStatus("A newer report snapshot was detected. Updating the dashboard...", true);

          var replaced = await replaceReportView(data.content || null);
          if (!(replaced)) {
            setStatus("A newer report snapshot was detected. Refreshing the dashboard...", true);
            currentWindow.location.reload();
            return;
          }

          return;
        }
      } catch (error) {
        pollFailures += 1;
        console.error("Application Artisan Report polling failed.", error);
      }

      await pause(1500);
    }

    if (pollFailures > 0) {
      clearBusyState("The dashboard could not confirm whether the report is still running after repeated status checks failed. Refresh this page later or check the server logs for command status.");
      return;
    }

    clearBusyState("The report command is still running in the background. Refresh this page later to load the next snapshot.");
  }

  async function runGenerateFlow(previousGeneratedOn, reason) {
    setStatus(reason || "Starting a new Application Artisan Report snapshot in a private command...", true);

    try {
      showPlatformLoading();
      await runDashboardAction("generate-report", {});
    } catch (error) {
      console.error("Application Artisan Report generation failed.", error);
      setStatus((error && (error.description || error.message)) || "The report command could not be started. Check the server logs for details.", false);
      return;
    } finally {
      hidePlatformLoading();
    }

    await pollForFreshReport(previousGeneratedOn || "");
  }

  function bindGenerateButtons() {
    var buttons = getGenerateButtons();

    for (var index = 0; index < buttons.length; index += 1) {
      var button = buttons[index];
      if (button.getAttribute("data-artisan-bound") === "true") {
        continue;
      }

      button.setAttribute("data-artisan-bound", "true");
      button.addEventListener("click", async function(event) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }

        if (this.disabled) {
          return;
        }

        await runGenerateFlow(latestGeneratedOn, "Starting a new Application Artisan Report snapshot in a private command...");
      }, true);
    }
  }

  if (!(dashboardApi)) {
    setStatus("Dashboard API was not available for this page. Rebuild and reload the dashboard.", false);
    return;
  }

  bindGenerateButtons();
  clearBusyState(status && status.textContent ? status.textContent : "");
  animatePopItems();
  animateNumbers();
};
